import { inject } from '@angular/core';
import type { Contact } from '@indutrust/shared-model';
import {
  withCrudEntitiesEvents,
  withLoadAllOnAuthEvents,
  withOrderedEntities,
} from '@indutrust/shared-util';
import { signalStore } from '@ngrx/signals';
import { ContactApiClient } from '../contact-api-client.service';
import { contactCrudEvents } from './contact.events';

/**
 * Signal store managing Contact entities with event-driven CRUD operations.
 *
 * Provides ordered entities and event-driven CRUD operations via {@link withCrudEntitiesEvents}.
 * API calls are automatically handled through the service factory passed to withCrudEntitiesEvents.
 * Automatically loads all contacts on authentication events.
 *
 * @example
 * import { inject } from '@angular/core';
 * import { ContactStore } from '@indutrust/shared-data';
 *
 * class Example {
 *   protected readonly contactStore = inject(ContactStore);
 *   // Auto-loads on auth via withLoadAllOnAuthEvents — read entitiesOrdered() in the template.
 *
 *   add() {
 *     this.contactStore.createRequested({ payload: { firstName: 'Ada', lastName: 'Lovelace' } });
 *   }
 * }
 */
export const ContactStore = signalStore(
  { providedIn: 'root' },
  withCrudEntitiesEvents<Contact>(contactCrudEvents, () =>
    inject(ContactApiClient)
  ),
  withLoadAllOnAuthEvents(contactCrudEvents),
  withOrderedEntities<Contact>()
);
